/**
 * Reads and syncs the selected Shopify selling plan on product pages.
 * Works with Recharge widgets (including Shadow DOM), Shopify selectors, and theme forms.
 */
(function () {
  window.utils = window.utils || {};

  const ROOT_ATTR = "data-product-subscription-root";

  function isSubscriptionShoppingEnabled() {
    return Boolean(
      document.querySelector(`[${ROOT_ATTR}][data-subscription-enabled="true"]`)
    );
  }

  function hasRechargeWidget() {
    return document.querySelector("recharge-subscription-widget") != null;
  }

  function readInputValue(input) {
    if (!input || input.disabled) return null;
    const value = String(input.value || "").trim();
    return value || null;
  }

  function isNumericPlanId(value) {
    return /^\d+$/.test(String(value || "").trim());
  }

  function queryDeepAll(root, selector) {
    const matches = [];
    const visit = (node) => {
      if (!node || typeof node.querySelectorAll !== "function") return;
      node.querySelectorAll(selector).forEach((el) => matches.push(el));
      node.querySelectorAll("*").forEach((el) => {
        if (el.shadowRoot) visit(el.shadowRoot);
      });
    };
    visit(root);
    return matches;
  }

  function getProductRoot(context) {
    if (context && context.closest) {
      const scoped = context.closest(`[${ROOT_ATTR}]`);
      if (scoped) return scoped;
    }
    return document.querySelector(`[${ROOT_ATTR}]`);
  }

  function hasCustomSubscriptionSelector(scope) {
    const root = scope || getProductRoot();
    return Boolean(root?.querySelector("[data-cellcore-subscription-selector]"));
  }

  function getCustomSubscriptionSelector(scope) {
    const root = scope || getProductRoot();
    return root?.querySelector("[data-cellcore-subscription-selector]") || null;
  }

  function isCustomOneTimeSelected(selector) {
    if (!selector) return false;
    const oneTimeInput = selector.querySelector(
      'input[data-cellcore-purchase-type][value="one_time"]'
    );
    return Boolean(oneTimeInput?.checked);
  }

  function readFromCustomSelector(scope) {
    const selector = getCustomSubscriptionSelector(scope);
    if (!selector) return null;
    if (isCustomOneTimeSelected(selector)) return null;

    const checkedPlan = selector.querySelector(
      'input[name="cellcore-selling-plan"]:checked'
    );
    const planId = readPlanIdFromElement(checkedPlan);
    if (planId) return planId;

    const fallbackPlan = selector.querySelector('input[name="cellcore-selling-plan"]');
    return readPlanIdFromElement(fallbackPlan);
  }

  function selectSellingPlan(selector, planInput) {
    if (!selector || !planInput) return;
    selector.querySelectorAll('input[name="cellcore-selling-plan"]').forEach((input) => {
      input.checked = input === planInput;
    });
  }

  function selectPurchaseType(selector, type) {
    if (!selector) return;
    selector.querySelectorAll("input[data-cellcore-purchase-type]").forEach((input) => {
      input.checked = input.value === type;
    });
  }

  function isMultiPlanSelector(selector) {
    if (!selector) return false;
    if (selector.classList.contains("cellcore-subscription-selector--multi-plan")) {
      return true;
    }
    const planCount = parseInt(selector.dataset.planCount || "0", 10);
    return planCount > 1;
  }

  function isMobilePlansLayout() {
    return window.matchMedia("(max-width: 749px)").matches;
  }

  function collapseMobilePlans(selector) {
    const plans = selector?.querySelector("[data-subscription-plans]");
    if (!plans) return;
    plans.classList.remove("is-expanded");
    plans.querySelectorAll("[data-plan-toggle]").forEach((toggle) => {
      toggle.setAttribute("aria-expanded", "false");
    });
  }

  function expandMobilePlans(selector) {
    const plans = selector?.querySelector("[data-subscription-plans]");
    if (!plans) return;
    plans.classList.add("is-expanded");
    const selectedPlan = plans.querySelector(
      ".cellcore-subscription-selector__plan.is-selected"
    );
    selectedPlan?.querySelector("[data-plan-toggle]")?.setAttribute("aria-expanded", "true");
  }

  function toggleMobilePlansExpanded(selector) {
    const plans = selector?.querySelector("[data-subscription-plans]");
    if (!plans) return;
    if (plans.classList.contains("is-expanded")) {
      collapseMobilePlans(selector);
    } else {
      expandMobilePlans(selector);
    }
  }

  function updateMobilePlansChrome(selector) {
    const plans = selector?.querySelector("[data-subscription-plans]");
    const host = selector?.querySelector("[data-plans-host]");
    if (!plans || !host) return;

    const selectedPlan = plans.querySelector(
      ".cellcore-subscription-selector__plan.is-selected"
    );
    const hasBadge = Boolean(
      selectedPlan?.classList.contains("has-best-value-badge")
    );

    host.classList.toggle("has-selected-badge", hasBadge && isMobilePlansLayout());

    if (!isMultiPlanSelector(selector)) return;

    plans.querySelectorAll(".cellcore-subscription-selector__plan").forEach((plan) => {
      const toggle = plan.querySelector("[data-plan-toggle]");
      if (!toggle) return;
      const isSelected = plan.classList.contains("is-selected");
      toggle.tabIndex = isSelected && isMobilePlansLayout() ? 0 : -1;
    });
  }

  function updateCustomSelectorUI() {
    const selector = getCustomSubscriptionSelector();
    if (!selector) return;

    const oneTimeSelected = isCustomOneTimeSelected(selector);
    selector.classList.toggle("is-one-time", oneTimeSelected);

    const subscribeCard = selector.querySelector("[data-subscription-card]");
    const oneTimeCard = selector.querySelector("[data-one-time-card]");
    subscribeCard?.classList.toggle("is-active", !oneTimeSelected);
    oneTimeCard?.classList.toggle("is-active", oneTimeSelected);

    selector.querySelectorAll(".cellcore-subscription-selector__plan").forEach((plan) => {
      const input = plan.querySelector('input[name="cellcore-selling-plan"]');
      plan.classList.toggle("is-selected", Boolean(input?.checked) && !oneTimeSelected);
    });

    updateMobilePlansChrome(selector);

    if (oneTimeSelected) return;

    const checkedPlan = selector.querySelector(
      'input[name="cellcore-selling-plan"]:checked'
    )?.closest(".cellcore-subscription-selector__plan");
    if (!checkedPlan) return;

    const planName = checkedPlan.dataset.planName || "";

    const subtitle = selector.querySelector("[data-subscription-subtitle]");
    if (subtitle && planName) {
      subtitle.textContent = planName;
    }
  }

  function initCustomSelectorInteractions(root) {
    const selector = root?.querySelector("[data-cellcore-subscription-selector]");
    if (!selector || selector.dataset.cellcoreInteractionsInit === "true") return;
    selector.dataset.cellcoreInteractionsInit = "true";

    const plans = selector.querySelector("[data-subscription-plans]");

    selector.querySelectorAll(".cellcore-subscription-selector__plan").forEach((plan) => {
      const input = plan.querySelector('input[name="cellcore-selling-plan"]');
      if (!input) return;

      plan.addEventListener("click", (event) => {
        if (event.target.closest("[data-plan-toggle]")) return;
        event.preventDefault();

        selectPurchaseType(selector, "subscription");

        const isSelected = plan.classList.contains("is-selected");
        const isExpanded = plans.classList.contains("is-expanded");
        const multiPlan = isMultiPlanSelector(selector);

        if (isMobilePlansLayout() && multiPlan) {
          if (!isExpanded && isSelected) {
            expandMobilePlans(selector);
            syncAll();
            return;
          }

          if (!isSelected) {
            selectSellingPlan(selector, input);
            collapseMobilePlans(selector);
          } else if (isExpanded) {
            collapseMobilePlans(selector);
            syncAll();
            return;
          }
        } else {
          selectSellingPlan(selector, input);
        }

        syncAll();
      });

      input.addEventListener("change", () => {
        if (!input.checked) return;
        selectPurchaseType(selector, "subscription");
        if (isMobilePlansLayout() && isMultiPlanSelector(selector)) {
          collapseMobilePlans(selector);
        }
        syncAll();
      });

      plan.querySelector("[data-plan-toggle]")?.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (
          !isMobilePlansLayout() ||
          !isMultiPlanSelector(selector) ||
          !plan.classList.contains("is-selected")
        ) {
          return;
        }
        toggleMobilePlansExpanded(selector);
      });
    });

    selector
      .querySelector(".cellcore-subscription-selector__header")
      ?.addEventListener("click", () => {
        selectPurchaseType(selector, "subscription");
        syncAll();
      });

    selector.querySelectorAll("input[data-cellcore-purchase-type]").forEach((input) => {
      input.addEventListener("change", () => {
        if (input.value === "one_time") {
          collapseMobilePlans(selector);
        }
        syncAll();
      });
    });

    window.matchMedia("(max-width: 749px)").addEventListener("change", () => {
      collapseMobilePlans(selector);
      updateMobilePlansChrome(selector);
    });
  }

  function readFromWidgetInputs(scope, skipInput) {
    if (!scope || typeof scope.querySelector !== "function") return null;

    const checkedRadio = scope.querySelector('input[name="selling_plan"]:checked');
    if (checkedRadio && checkedRadio !== skipInput) {
      const fromRadio = readInputValue(checkedRadio);
      if (fromRadio) return fromRadio;
    }

    const checkedOption = scope.querySelector(
      'input[data-radio-type="selling_plan"]:checked, input[data-selling-plan-id]:checked'
    );
    if (checkedOption) {
      const planId =
        checkedOption.getAttribute("data-selling-plan-id") || checkedOption.value;
      if (planId) return String(planId).trim();
    }

    const select = scope.querySelector('select[name="selling_plan"]');
    if (select) {
      const fromSelect = readInputValue(select);
      if (fromSelect) return fromSelect;
    }

    return null;
  }

  function readPlanIdFromElement(element) {
    if (!element) return null;

    const candidates = [
      element.value,
      element.getAttribute("value"),
      element.dataset?.sellingPlanId,
      element.getAttribute("data-selling-plan-id"),
      element.getAttribute("data-selling_plan"),
    ];

    for (const candidate of candidates) {
      if (isNumericPlanId(candidate)) return String(candidate).trim();
    }

    return null;
  }

  function isRechargeOneTimeSelected(shadowRoot) {
    const oneTimeOption = shadowRoot.querySelector(
      '[part="rc-purchase-option__onetime"], .rc-purchase-option__onetime'
    );
    if (!oneTimeOption) return false;

    return Boolean(
      oneTimeOption.matches(
        '[part="rc-purchase-option__selected"], .rc-purchase-option__selected'
      ) ||
        oneTimeOption.querySelector(
          '[part="rc-purchase-option__selected"], .rc-purchase-option__selected, input[type="radio"]:checked'
        )
    );
  }

  function isRechargeOneTimePurchaseActive() {
    const widgets = document.querySelectorAll("recharge-subscription-widget");
    for (const widget of widgets) {
      if (widget.shadowRoot && isRechargeOneTimeSelected(widget.shadowRoot)) {
        return true;
      }
    }
    return false;
  }

  function isRechargeSubscriptionActive(shadowRoot) {
    const subscriptionOption = shadowRoot.querySelector(
      '[part="rc-purchase-option__subscription"], .rc-purchase-option__subscription'
    );
    if (!subscriptionOption) return false;

    return Boolean(
      subscriptionOption.matches(
        '[part="rc-purchase-option__selected"], .rc-purchase-option__selected'
      ) ||
        subscriptionOption.querySelector(
          '[part="rc-purchase-option__selected"], .rc-purchase-option__selected, input[type="radio"]:checked'
        )
    );
  }

  function readFromRechargeShadowRoot(shadowRoot) {
    if (!shadowRoot || isRechargeOneTimeSelected(shadowRoot)) return null;
    if (!isRechargeSubscriptionActive(shadowRoot)) return null;

    const checkedNamedInputs = queryDeepAll(
      shadowRoot,
      'input[name="selling_plan"]:checked, input[data-selling-plan-id]:checked'
    );
    for (const input of checkedNamedInputs) {
      const planId = readPlanIdFromElement(input);
      if (planId) return planId;
    }

    const selects = queryDeepAll(
      shadowRoot,
      'select[name="selling_plan"], select.rc-plans-dropdown, [part="rc-plans-select"]'
    );
    for (const select of selects) {
      const planId = readPlanIdFromElement(select);
      if (planId) return planId;
    }

    const selectedButtons = queryDeepAll(
      shadowRoot,
      '[part="rc-plans-button__selected"], .rc-plans-button__selected, button[aria-pressed="true"]'
    );
    for (const button of selectedButtons) {
      const planId = readPlanIdFromElement(button);
      if (planId) return planId;
    }

    const checkedRadios = queryDeepAll(shadowRoot, 'input[type="radio"]:checked');
    for (const radio of checkedRadios) {
      const planId = readPlanIdFromElement(radio);
      if (planId) return planId;
    }

    return null;
  }

  function readFromRechargeWidgets() {
    const widgets = document.querySelectorAll("recharge-subscription-widget");
    for (const widget of widgets) {
      const planId = readFromRechargeShadowRoot(widget.shadowRoot);
      if (planId) return planId;
    }
    return null;
  }

  function readFromLegacyRecharge() {
    const scopes = [
      document.querySelector(".rc-widget-injection-parent"),
      document.querySelector(".rc-container"),
      document.querySelector(".rc-widget"),
    ].filter(Boolean);

    for (const scope of scopes) {
      const dropdown = scope.querySelector(".rc-selling-plans__dropdown");
      const fromDropdown = readPlanIdFromElement(dropdown);
      if (fromDropdown) return fromDropdown;

      const fromInputs = readFromWidgetInputs(scope);
      if (fromInputs) return fromInputs;
    }

    return null;
  }

  function resolveSelectedSellingPlan(skipInput) {
    if (!isSubscriptionShoppingEnabled()) return null;

    const root = getProductRoot();
    if (hasCustomSubscriptionSelector(root)) {
      return readFromCustomSelector(root);
    }

    if (hasRechargeWidget()) {
      if (isRechargeOneTimePurchaseActive()) return null;
      return readFromRechargeWidgets();
    }

    const fromLegacy = readFromLegacyRecharge();
    if (fromLegacy) return fromLegacy;

    if (root) {
      const fromRoot = readFromWidgetInputs(root, skipInput);
      if (fromRoot) return fromRoot;
    }

    return null;
  }

  function syncCanonicalInput() {
    const root = getProductRoot();
    if (!root) return;

    const canonical = root.querySelector("[data-selling-plan-input]");
    if (!canonical) return;

    const nextValue = resolveSelectedSellingPlan(canonical) || "";
    if (canonical.value !== nextValue) {
      canonical.value = nextValue;
    }
  }

  function loadProductPlanData() {
    const node = document.querySelector("[data-product-selling-plan-data]");
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.warn("Selling plan product data parse skipped:", error);
      return null;
    }
  }

  function formatMoney(cents) {
    const amount = Number(cents || 0);
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(amount);
    }
    return "$" + (amount / 100).toFixed(2);
  }

  function getAllocationForVariant(variantId, planId) {
    if (!variantId || !planId) return null;
    const data = loadProductPlanData();
    const variant = data?.variants?.[String(variantId)];
    if (!variant) return null;
    return variant.allocations?.[String(planId)] || null;
  }

  function getSelectedAllocation(context) {
    const root = getProductRoot(context);
    const planId = getSelectedSellingPlan(context);
    if (!planId) return null;

    const atcButton = root?.querySelector(".product-cart-add.retail");
    const variantId = atcButton?.dataset?.variantId;
    if (!variantId) return null;

    return getAllocationForVariant(variantId, planId);
  }

  function updateAtcButtonPrices() {
    if (!isSubscriptionShoppingEnabled()) return;

    const root = getProductRoot();
    if (!root) return;

    const planId = resolveSelectedSellingPlan(
      root.querySelector("[data-selling-plan-input]")
    );

    root.querySelectorAll(".product-cart-add.retail").forEach((button) => {
      if (!button.closest(`[${ROOT_ATTR}][data-subscription-enabled="true"]`)) return;

      const variantId = button.dataset.variantId;
      const basePrice = parseInt(button.dataset.basePrice || "0", 10);
      const priceNode = button.querySelector("[data-product-atc-price]");
      if (!priceNode || !variantId) return;

      let priceCents = basePrice;
      if (planId) {
        const allocation = getAllocationForVariant(variantId, planId);
        if (allocation?.price != null) {
          priceCents = allocation.price;
        }
      }

      button.dataset.price = String(priceCents);
      priceNode.textContent = formatMoney(priceCents);
    });
  }

  function syncAll() {
    updateCustomSelectorUI();
    syncCanonicalInput();
    updateAtcButtonPrices();
  }

  const debouncedSync = (() => {
    let timeoutId;
    return () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(syncAll, 50);
    };
  })();

  function initSellingPlanSync(root) {
    if (!root || root.dataset.sellingPlanInit === "true") return;
    root.dataset.sellingPlanInit = "true";
    initCustomSelectorInteractions(root);
    syncAll();
  }

  function initDocumentSync() {
    if (document.body.dataset.sellingPlanDocumentInit === "true") return;
    document.body.dataset.sellingPlanDocumentInit = "true";

    document.addEventListener("change", debouncedSync, true);
    document.addEventListener("click", debouncedSync, true);

    const observer = new MutationObserver(debouncedSync);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["value", "checked", "aria-pressed", "class"],
    });

    debouncedSync();
  }

  function getSelectedSellingPlan(context) {
    if (!isSubscriptionShoppingEnabled()) return null;

    syncCanonicalInput();
    const root = getProductRoot(context);
    const canonical = root?.querySelector("[data-selling-plan-input]");
    return readInputValue(canonical);
  }

  function debugSellingPlan() {
    const widgets = [...document.querySelectorAll("recharge-subscription-widget")];
    const customSelector = getCustomSubscriptionSelector();
    return {
      enabled: isSubscriptionShoppingEnabled(),
      customSelector: Boolean(customSelector),
      customOneTimeSelected: isCustomOneTimeSelected(customSelector),
      customSelected: readFromCustomSelector(),
      oneTimeSelected: isRechargeOneTimePurchaseActive(),
      selected: getSelectedSellingPlan(),
      allocation: getSelectedAllocation(),
      canonical: document.querySelector("[data-selling-plan-input]")?.value || "",
      rechargeWidgetCount: widgets.length,
      rechargeHasShadowRoot: widgets.map((widget) => Boolean(widget.shadowRoot)),
      rechargeSelected: readFromRechargeWidgets(),
      legacySelected: hasRechargeWidget() ? null : readFromLegacyRecharge(),
      pageInputs: [
        ...document.querySelectorAll('[name="cellcore-selling-plan"]'),
        ...document.querySelectorAll('[name="selling_plan"]'),
      ].map((input) => ({
        value: input.value,
        checked: input.checked,
        inSubscriptionRoot: Boolean(input.closest("[data-product-subscription-root]")),
      })),
    };
  }

  function init() {
    if (!isSubscriptionShoppingEnabled()) return;
    document.querySelectorAll(`[${ROOT_ATTR}]`).forEach(initSellingPlanSync);
    initDocumentSync();
  }

  window.utils.sellingPlan = {
    getSelectedSellingPlan,
    getSelectedAllocation,
    initSellingPlanSync,
    debug: debugSellingPlan,
  };

  document.addEventListener("shopify:section:load", (event) => {
    event.target.querySelectorAll(`[${ROOT_ATTR}]`).forEach(initSellingPlanSync);
    debouncedSync();
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
